import { useTranslation } from 'next-i18next'
import React, { useEffect, useState } from 'react'

import { API_URL } from '@goodfellas/environment'

import { goodfellasApi } from '../../../apis'
import { useFeedbackStore, useStore } from '../../../functions/store'
import { FighterType } from '../../../types/fighter'
import FighterInfo from './fighterInfo'
import GameSkills from './gameSkills'
import GameStyleSlider from './gameStyleSlider'
import PersonalSkills from './personalSkills'

type FightersCardsProps = {
  /** When true, submit to POST /evaluation/feedback/global (evaluation by profile, no group) */
  submitToGlobal?: boolean
  /** When submitToGlobal, profileUserUuid of the single target for the payload */
  profileUserUuidForGlobal?: string
}

const FightersCards = ({
  submitToGlobal,
  profileUserUuidForGlobal,
}: FightersCardsProps = {}) => {
  const userStore = useStore((state) => state)
  const store = useFeedbackStore((state) => state)
  const index = store.index
  const { indexNext, indexPrev, checkIfRated } = store.feedbackActions

  const { t } = useTranslation('avaliacao360')

  // Run only when the set of fighters changes (e.g. feedback data loaded), not when
  // checkIfRated updates isRated (which would change store.ratings reference and re-trigger loop).
  const ratingsIdentity =
    store.ratings.length > 0
      ? store.ratings.map((r) => r.fighterPhone).join(',')
      : ''
  useEffect(() => {
    if (store.ratings.length === 0) return
    store.ratings.forEach((rating) => {
      checkIfRated(rating.fighterPhone)
    })
  }, [ratingsIdentity, checkIfRated, store.ratings])

  const ratings = store.ratings
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    setCurrentIndex(store.index ?? 0)
  }, [store.index])

  useEffect(() => {
    const el = document.getElementsByClassName('container')[0]
    if (el) el.scrollIntoView({ behavior: 'smooth' })
  }, [currentIndex])

  // Safety check: return null if ratings is empty or index is out of bounds
  if (!ratings || ratings.length === 0) {
    return (
      <div className="text-white text-center py-10">
        <p>Carregando avaliações...</p>
      </div>
    )
  }

  // Ensure index is within bounds
  const safeIndex = Math.min(Math.max(0, index), ratings.length - 1)
  const currentRating = ratings[safeIndex]

  if (!currentRating) {
    return (
      <div className="text-white text-center py-10">
        <p>Nenhuma avaliação disponível.</p>
      </div>
    )
  }

  const payloadAdapter = (store, forGlobal = false) => {
    return {
      name: store.name,
      date: new Date().toISOString(),
      phoneNumber: store.phoneNumber,
      belt: store.belt,
      degree: store.degree,
      type: store.type,
      ratings: ratings.map((rating, i) => {
        const r: Record<string, unknown> = {
          fighterPhone: rating.fighterPhone,
          fighterName: rating.fighterName,
          fighterBelt: rating.fighterBelt,
          fighterBeltDegree: rating.fighterBeltDegree,
          fighterType: rating.fighterType,
          gameStyle: rating.gameStyle,
          pointsLeft: rating.pointsLeft,
          personalSkills: {
            agility: rating.personalSkills.agility,
            commitment: rating.personalSkills.commitment,
            focus: rating.personalSkills.focus,
            stamina: rating.personalSkills.stamina,
            strength: rating.personalSkills.strength,
            technique: rating.personalSkills.technique,
          },
          gameSkills: {
            guard: rating.gameSkills.guard,
            guardPassing: rating.gameSkills.guardPassing,
            guardRecovery: rating.gameSkills.guardRecovery,
            positionDefense: rating.gameSkills.positionDefense,
            submission: rating.gameSkills.submission,
            submissionDefense: rating.gameSkills.submissionDefense,
            sweep: rating.gameSkills.sweep,
            takedown: rating.gameSkills.takedown,
          },
        }
        if (forGlobal && i === 0 && profileUserUuidForGlobal) {
          r.fighterProfileUserUuid = profileUserUuidForGlobal
        }
        return r
      }),
    }
  }

  const sendFeedback = async () => {
    const loader = document.getElementById('spinningLoader')
    loader?.classList.toggle('hidden')
    loader?.classList.toggle('flex')

    const payload = payloadAdapter(store, submitToGlobal === true)

    try {
      if (submitToGlobal) {
        await goodfellasApi.post(
          `${API_URL}evaluation/feedback/global`,
          payload,
        )
      } else {
        await goodfellasApi.post(`${API_URL}evaluation/feedback`, {
          ...payload,
          groupId: userStore?.group?._id,
        })
      }

      loader?.classList.toggle('hidden')
      loader?.classList.toggle('flex')
    } catch (error) {
      console.error('Error sending feedback:', error)
    }
  }

  return (
    <div id="fighterCardsContainer">
      <div id="spinningLoader" className="hidden">
        <div>{t('feedback.fighterCards.savingStatus')}</div>
      </div>
      <div id="feedbackMessage" className="hidden">
        <div>{t('feedback.fighterCards.savedStatus')}</div>
      </div>
      <div
        className={`feedback-card bg-white/20 border-1 border-white/15 px-4 md:px-8 pb-4 rounded-3xl shadow-lg mx-auto md:mx-0 md:mb-10 backdrop-blur-sm text-black lg:max-w-[900px] w-full w-90 md:w-auto`}
        id={`fighter${safeIndex.toString()}`}
        key={safeIndex}
      >
        <FighterInfo
          key={safeIndex}
          name={currentRating.fighterName}
          belt={currentRating.fighterBelt}
          beltDegree={currentRating.fighterBeltDegree}
          phone={currentRating.fighterPhone}
          type={currentRating.fighterType || FighterType.STUDENT}
        />
        {currentRating.fighterType === FighterType.OTHER ? (
          <>
            <p className="mb-5 text-lg text-white">
              {t('feedback.fighterCards.skipEvaluation')}
            </p>
          </>
        ) : (
          <>
            <GameStyleSlider
              fighter={currentRating.fighterPhone}
              index={safeIndex}
            />
            <PersonalSkills
              fighter={currentRating.fighterPhone}
              index={safeIndex}
            />
            <GameSkills
              fighter={currentRating.fighterPhone}
              index={safeIndex}
            />
          </>
        )}

        <div
          className={`flex flex-row ${
            safeIndex > 0 && safeIndex < ratings.length - 1
              ? 'justify-between'
              : safeIndex === 0
              ? 'justify-end'
              : safeIndex === ratings.length - 1
              ? 'justify-between'
              : ''
          } md:gap-5 gap:1`}
        >
          {safeIndex > 0 && (
            <button
              type="button"
              className="bg-white-100 hover:bg-white-300 border-1 border-white/80 text-white/80 focus:outline-none font-xs rounded-full w-full h5 sm:w-auto sm:px-5 py-1.5 text-center transition-all duration-300 ease-in-out display-inline-block cursor-pointer shadow-lg hover:shadow-xl"
              onClick={() => {
                sendFeedback()
                indexPrev()
              }}
            >
              {t('feedback.fighterCards.prevButton')}
            </button>
          )}

          {safeIndex < ratings.length - 1 && (
            <button
              type="button"
              className="text-white bg-red-100 hover:bg-red-300 focus:outline-none font-medium rounded-full w-full h5 sm:w-auto px-4 sm:px-10 py-3.5 text-center transition-all duration-300 ease-in-out display-inline-block cursor-pointer shadow-lg hover:shadow-xl"
              onClick={() => {
                sendFeedback()
                indexNext()
              }}
            >
              {t('feedback.fighterCards.nextButton')}
            </button>
          )}

          {safeIndex === ratings.length - 1 && (
            <button
              type="button"
              className="text-white bg-red-100 hover:bg-red-300 focus:outline-none font-medium rounded-full w-full h5 sm:w-auto px-4 sm:px-10 py-3.5 text-center transition-all duration-300 ease-in-out display-inline-block cursor-pointer shadow-lg hover:shadow-xl"
              onClick={() => sendFeedback()}
            >
              {t('feedback.fighterCards.submitButton')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default FightersCards
